import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Flex } from 'rebass';
import join from 'url-join';
import { Button, TextStyled, useTokens } from '@nike/nike-design-system-components';

import TableClientSide from '../../components/tableClientSide/TableClientSide';

import { getProductColumns } from './productColumns';

import { Icon } from '../../components';
import IconMenu from '../../components/IconMenu';
import AddProductsModal from '../catalogs/catalogsCreateOrEdit/modals/AddProductsModal';
import StatusModal from '../common/StatusModal';
import { showToast } from '../../components/toast/actions';

import useModal from '../../hooks/useModal';

import { SALES_ORG_TO_REGION } from '../catalogs/constants';

import { TranslatedCopy } from '../../intl';
import { http } from '../../util/index';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

const handleError = error =>
  showToast({
    message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
    type: 'error',
    error
  });

const Products = () => {
  const [productCodes, setProductCodes] = useState([]);
  const [regionCodes, setRegionCodes] = useState([]);
  const [products, setProducts] = useState({});
  const [findProductsCursor, setFindProductsCursor] = useState('-1');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isAddProductsModalOpen, openAddProductsModal, closeAddProductsModal] = useModal();
  const [isStatusModalOpen, openStatusModal, closeStatusModal] = useModal();
  const [statusModalProps, setStatusModalProps] = useState({ title: '' });

  const successModalProps = {
    type: 'success',
    title: 'Success:',
    message: 'The selected products were updated.'
  };

  const errorModalProps = {
    type: 'error',
    title: 'Error:',
    message: 'The selected products were unable to be updated, please try again.'
  };

  const dispatch = useDispatch();

  const tokens = useTokens();

  const memoizedColumns = useMemo(() => getProductColumns(), []);

  const setProductsAsNonReturnable = async selectedProducts =>
    http
      .post(
        join(api2, '/createNonreturnableProducts'),
        { products: selectedProducts },
        { 'Content-Type': 'application/json' }
      )
      .then(() => {
        selectedProducts.forEach(({ productCode }) => {
          products[productCode].isReturnable = false;
        });
        setProducts({ ...products });
        setIsLoading(false);
        setStatusModalProps(successModalProps);
      })
      .catch(() => {
        setIsLoading(false);
        setStatusModalProps(errorModalProps);
      });

  const setProductsAsReturnable = async selectedProducts =>
    http
      .delete(
        join(api2, '/nonreturnableProducts'),
        { products: selectedProducts },
        { 'Content-Type': 'application/json' }
      )
      .then(() => {
        selectedProducts.forEach(({ productCode }) => {
          products[productCode].isReturnable = true;
        });
        setProducts({ ...products });
        setIsLoading(false);
        setStatusModalProps(successModalProps);
      })
      .catch(() => {
        setIsLoading(false);
        setStatusModalProps(errorModalProps);
      });

  const findProducts = async payload =>
    http
      .post(join(api2, '/products'), { ...payload, languageCodes: ['EN'] })
      .then(({ data: response }) => {
        const {
          objects,
          pages: { cursor }
        } = response;
        const initialCall = !payload.cursor;

        if (cursor === '-1') {
          if (initialCall) {
            setProducts({});
          }
          setIsLoading(false);
          return;
        }

        const newProductsObj = {};
        objects.forEach(o => {
          newProductsObj[o.productCode] = o;
        });

        if (initialCall) {
          setProducts(newProductsObj);
        } else {
          setProducts({ ...products, ...newProductsObj });
        }

        setFindProductsCursor(cursor);
      })
      .catch(error => {
        setIsLoading(false);
        setFindProductsCursor('-1');
        const missingCodes = payload.productCodes.filter(p => !products[p]).join(',');
        setErrorMessage(`Product codes not fetched: ${missingCodes}`);
        dispatch(handleError(error));
      });

  useEffect(() => {
    if (findProductsCursor !== '-1') {
      findProducts({ productCodes, regionCodes, cursor: findProductsCursor });
    }
  }, [findProductsCursor]);

  const addProductsAction = async (productCodes, salesOrg) => {
    setIsLoading(true);
    setProductCodes(productCodes);
    const regionCodes = [SALES_ORG_TO_REGION[salesOrg]];
    setRegionCodes(regionCodes);
    await findProducts({ productCodes, regionCodes });
  };

  const getProductsActionItems = ({ selectedFlatRows, closeModal }) => {
    const selectedProducts = selectedFlatRows
      ?.map(row => row.original)
      ?.map(row => ({ productCode: row.productCode, salesOrg: row.salesOrganizationCode }));

    return {
      'Non-Returnable': {
        modal: {
          actions: [
            { actionName: 'Cancel', action: closeModal },
            {
              actionName: 'Non-Returnable',
              action:
                ({ toggleIsLoading }) =>
                async () => {
                  toggleIsLoading(true, 'Flagging as non-returnable...');

                  setIsLoading(true);
                  await setProductsAsNonReturnable(selectedProducts);

                  toggleIsLoading();
                  closeModal();
                  openStatusModal();
                }
            }
          ],
          content: () => 'Do you want to proceed with making the selected product/s non-returnable?',
          title: 'Non-Returnable'
        }
      },
      Returnable: {
        modal: {
          actions: [
            { actionName: 'Cancel', action: closeModal },
            {
              actionName: 'Returnable',
              action:
                ({ toggleIsLoading }) =>
                async () => {
                  toggleIsLoading(true, 'Flagging as returnable...');

                  setIsLoading(true);
                  await setProductsAsReturnable(selectedProducts);

                  toggleIsLoading();
                  closeModal();
                  openStatusModal();
                }
            }
          ],
          content: () => 'Do you want to proceed with making the selected product/s returnable?',
          title: 'Returnable'
        }
      }
    };
  };

  const ProductsIconMenu = (
    <IconMenu align='right' menuOptions={[{ text: 'Add Products', onClick: openAddProductsModal }]} />
  );

  return (
    <Box style={{ backgroundColor: tokens.podiumCdsColorGrey50 }}>
      <Flex
        sx={{
          borderBottom: `1px solid ${tokens.podiumCdsColorGrey200}`,
          padding: '12px',
          width: '100%'
        }}
      >
        <TextStyled appearance='title2'>Products</TextStyled>
      </Flex>

      {errorMessage && (
        <Flex p='12px' width='100%' flexDirection='row'>
          <Button onClick={() => setErrorMessage('')}>
            <Icon name='x' alt='X' />
          </Button>
          <TextStyled appearance='body2' color='error'>
            {errorMessage}
          </TextStyled>
        </Flex>
      )}

      <TableClientSide
        columns={memoizedColumns}
        data={Object.values(products)}
        isLoading={isLoading}
        getActionItems={getProductsActionItems}
        rightHeaderSlot={ProductsIconMenu}
        includeRowSelect
        fullHeight
      />

      <AddProductsModal
        title='Add Products'
        closeModal={closeAddProductsModal}
        isOpen={isAddProductsModalOpen}
        addProductsAction={addProductsAction}
        hasSalesOrg
      />

      <StatusModal isOpen={isStatusModalOpen} closeModal={closeStatusModal} modalProps={statusModalProps} />
    </Box>
  );
};

export default Products;
